/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-07-11
 */

import React, { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import avatarPlaceholder from '../components/images/avatar-placeholder.png';
import { baseUrl, debug, fetchRecords, postFormData, ReceiptType } from "../utils/AppUtil";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { SearchDeceasedRecord } from "../components/SearchDeceasedRecord";

type FormDataType = {
    firstName: string,
    lastName: string,
    subscriberEmail: string,
    telephone: string,

    subscriberId: string,
    deceasedIds: string[],
    currencyId: string,
    monthlyAmount: number,
    prepaidAmount: number,
    months: number,
    prePaid: boolean,
}

type DeceasedResult = {
    deceasedId: string,
    firstName: string,
    lastName: string,
    middleName: string,
    gender: string,
    cemeteryName: string,
    birthDate: string,
    birthDateDisplay: string,
    deathDate: string,
    deathDateDisplay: string,
    yahrzeitDat: string,
    yahrzeitDateDisplay:string,
    yahrzeitDesc:string,
    profilePicture: string
}

type CurrencyType = {
    id: string,
    name: string,
    code: string,
    symbol: string,
    position: string,
    decimalPlaces: number,
    deletedInd: boolean
}

export const GraveMaintenance: React.FC = () => {

    const formData: FormDataType = {
        firstName: "",
        lastName: "",
        subscriberEmail: "",
        telephone: "",

        deceasedIds: [],
        subscriberId: "",
        currencyId: "",
        monthlyAmount: 0,
        prepaidAmount: 0,
        months: 0,
        prePaid: false
    }

    const [formState, setFormState] = useState<FormDataType>(formData);
    const [deceasedList, setDeceasedList] = useState<DeceasedResult[]>([]);
    const [deceasedListSelected, setDeceasedListSelected] = useState<string[]>([]);
    const [deceasedListJSX, setDeceasedListJSX] = useState<React.ReactElement[]>();
    const [isOnceOff, setIsOnceOff] = useState<boolean>(true);
    const [currency, setCurrency] = useState<CurrencyType>();
    const [payForm, setPayForm] = useState<React.ReactElement[]>();
    const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

    const [formBody, setFormBody]: any = useState(true);
    const [formMessageSuccess, setFormMessageSuccess]: any = useState(false);
    const [formMessageError, setFormMessageError]: any = useState(false);

    const payFormRef = useRef<HTMLFormElement>(null);

    const {search} = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(search), [search]);

    useEffect( () => {
        fetchRecords(`deceased/?page=1&size=100`, processData);
    }, []);

    useEffect(() => {
        setDeceasedListJSX(buildListJSX(deceasedListSelected));
        setFormState((formState) => ({
            ...formState, 
            deceasedIds: deceasedListSelected
        }));
        
    }, [deceasedListSelected]);

    useEffect(() => {
        setCurrency(currency);
        const receiptId: string | null = searchParams.get("receiptId");
        if(receiptId) {
            fetchRecords(`receipts/${receiptId}`, processReceipt)
            .catch((error) => {
                console.log(error.message);
            });
        } else {
            fetchRecords("currency", processCurrencyData)
            .catch((error) => {
                console.log(error.message);
            });
        }

    }, []);
    
    useEffect(() => {
        if(payFormRef.current){
            payFormRef.current.submit();
        }
    }, [payForm]);

    const processCurrencyData = (responseJson: CurrencyType[]) => {
        
        let zar = {} as CurrencyType;
        let usd = {} as CurrencyType;
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        for(const record in responseJson) {
            if (responseJson[record].code === "ZAR") {
                zar = responseJson[record];
            }
            if (responseJson[record].code === "USD") {
                usd = responseJson[record];
            }            
        }

        if (timeZone === "Africa/Johannesburg") {
            setCurrency(zar);
            formData.currencyId = zar.id;
        } else {
            setCurrency(usd);
            formData.currencyId = usd.id;
        }
        console.log("currency:", currency);
        setFormState(formData);
    }

    const getPaymentData = (response: any) => {
        fetchRecords(`receipts/${response.receiptId}/paymentData`, processPaymentData)
        .catch((error) => {
            console.log(error.message);
        });
    }

    const processPaymentData = (response: any) => {
        const action: string  = response["action"];
        const method: string  = response["method"];

        const data: Map<string, string | null> = new Map<string, string | null>();

        for(const key in response["data"]) {
            const property: any = response["data"][key];
            data.set(property.name, property.value);
        }

        let pfFormInputs: ReactElement[] = [];
        let i: number = 0
        data.forEach((value: string | null, key: string) => {
            if(value != null && value !== "") {
                pfFormInputs.push(
                    <input key={i++} name={key} type="hidden" value={value.trim()} />
                );                
            }
        });
        pfFormInputs.push(
            <input type="submit" value="Proceed to payment ..." style={{visibility: "hidden"}}/>
        );

        let pfForm: ReactElement[] = [];
        pfForm.push(
            <form ref={payFormRef} action={action} method={method} >
                {pfFormInputs}
            </form>
        );

        setPayForm(pfForm);
    }

    const buildListJSX = (data: string[]): React.ReactElement[] => {
        
        const result: React.ReactElement[] = [];
        let key: number = 0;
        data.forEach((deceasedId: string) => {

            const currentDeceased: DeceasedResult | undefined = deceasedList.find(deceased => deceased.deceasedId === deceasedId)
            
            let ageAtDeath: any = "";
            if (currentDeceased?.birthDateDisplay) {
                const birthDate = currentDeceased.birthDateDisplay;
                const deathDate = currentDeceased.deathDateDisplay;
    
                let birthDateYear: any = "";
                if (birthDate) {
                    birthDateYear = birthDate.slice(birthDate.length - 4);
                }
        
                let deathDateYear: any = "";
                if (deathDate) {
                    deathDateYear = deathDate.slice(deathDate.length - 4);
                }
        
                ageAtDeath = (deathDateYear - birthDateYear);
                
            } else {
                ageAtDeath = "Unavailable";
            }
    
            let imageSrc = avatarPlaceholder;
            if (currentDeceased?.profilePicture) {
                imageSrc = (baseUrl + currentDeceased.profilePicture);
            }

            result.push(
                <li key={key} className="search-results list-group-item mb-3">    
                    {key === 0 &&
                        <div className="mb-2">
                            <p style={{whiteSpace: 'nowrap', textDecoration: 'underline'}}><b>First Grave&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp; R180/month,&nbsp;&nbsp;R2160/year</b></p>
                        </div>
                    }


                    {key === 1 &&
                        <div className="mb-2 mt-5">
                            <p style={{whiteSpace: 'nowrap', textDecoration: 'underline'}}><b>Additional Graves&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp; R90/month,&nbsp;&nbsp;R1080/year</b></p>
                        </div>
                    }

                    <div className="search-details d-flex justify-content-between gap-3 align-items-center">
                        <div className="d-flex justify-content-between align-items-center w-100 flex-wrap">
                            <div className="d-flex align-items-center gap-3">
                                <svg onClick={() => handleRemoveDeceased(currentDeceased?.deceasedId as string)} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-x-lg" viewBox="0 0 16 16">
                                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
                                </svg>

                                <a href={`/deceased/${currentDeceased?.deceasedId}`} className="d-flex gap-3 align-items-center jc-txt-blue fs-6 fw-bold">
                                    <img src={imageSrc} className="recent-image" />
                                    {currentDeceased?.firstName + ' ' + currentDeceased?.lastName}
                                </a>
                            </div>
                            
                            <p className="fs-7 fw-bold">Death Date: {currentDeceased?.deathDateDisplay ? currentDeceased?.deathDateDisplay : "Unavailable"}</p>
                        </div>
    
                        <button className="search-drop-svg" data-bs-toggle="collapse" data-bs-target={`#${currentDeceased?.deceasedId}`} aria-expanded="false" aria-controls="collapseExample">
                            <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_3957_64331)">
                                    <rect width="20" height="20" transform="translate(0 0.00195312)" fill="white" fillOpacity="0.01"/>
                                    <path fill-rule="evenodd" clipRule="evenodd" d="M2.05666 5.80862C2.11472 5.75041 2.18369 5.70423 2.25962 5.67273C2.33555 5.64122 2.41696 5.625 2.49916 5.625C2.58137 5.625 2.66277 5.64122 2.73871 5.67273C2.81464 5.70423 2.88361 5.75041 2.94166 5.80862L9.99916 12.8674L17.0567 5.80862C17.1148 5.75051 17.1838 5.70441 17.2597 5.67296C17.3356 5.64151 17.417 5.62533 17.4992 5.62533C17.5813 5.62533 17.6627 5.64151 17.7386 5.67296C17.8146 5.70441 17.8836 5.75051 17.9417 5.80862C17.9998 5.86673 18.0459 5.93571 18.0773 6.01164C18.1088 6.08756 18.125 6.16894 18.125 6.25112C18.125 6.3333 18.1088 6.41467 18.0773 6.4906C18.0459 6.56652 17.9998 6.63551 17.9417 6.69362L10.4417 14.1936C10.3836 14.2518 10.3146 14.298 10.2387 14.3295C10.1628 14.361 10.0814 14.3772 9.99916 14.3772C9.91695 14.3772 9.83555 14.361 9.75962 14.3295C9.68369 14.298 9.61472 14.2518 9.55666 14.1936L2.05666 6.69362C1.99846 6.63556 1.95228 6.56659 1.92077 6.49066C1.88927 6.41473 1.87305 6.33333 1.87305 6.25112C1.87305 6.16891 1.88927 6.08751 1.92077 6.01157C1.95228 5.93564 1.99846 5.86667 2.05666 5.80862Z" fill="#463D3D"/>
                                </g>
                                <defs>
                                    <clipPath id="clip0_3957_64331">
                                        <rect width="20" height="20" fill="white" transform="translate(0 0.00195312)"/>
                                    </clipPath>
                                </defs>
                            </svg>
                        </button>
    
                    </div>
    
                    <div className="collapse" id={currentDeceased?.deceasedId}>
                        <div className="d-block d-sm-none">
                            <div className="d-flex gap-3 flex-wrap py-3">
                                <div className="d-flex gap-3 flex-column">
                                    <p className="fs-7"><span className="fw-bold">Gender</span><br/>{currentDeceased?.gender}</p>
                                    <p className="fs-7"><span className="fw-bold">Age at Death</span><br/>{ageAtDeath}</p>
                                </div>

                                <div className="d-flex gap-3 flex-column">
                                    <p className="fs-7"><span className="fw-bold">Date of Birth</span><br/>{currentDeceased?.birthDateDisplay ? currentDeceased?.birthDateDisplay: "Unavailable"}</p>
                                    <p className="fs-7"><span className="fw-bold">Cemetery</span><br/>{currentDeceased?.cemeteryName}</p>
                                </div>

                                <div className="d-flex gap-3 flex-column">
                                    <p className="fs-7"><span className="fw-bold">Yahrzeit</span><br/>{currentDeceased?.yahrzeitDesc ? currentDeceased?.yahrzeitDesc : "Unavailable"} {currentDeceased?.yahrzeitDateDisplay && "(" +  currentDeceased?.yahrzeitDateDisplay + ")"}</p>
                                </div>
                            </div>
                        </div>

                        <div className="d-none d-sm-block">
                            <div className="d-flex gap-5 flex-wrap py-3">
                                <p className="fs-7"><span className="fw-bold">Gender</span><br/>{currentDeceased?.gender}</p>
                                <p className="fs-7"><span className="fw-bold">Age at Death</span><br/>{ageAtDeath}</p>
                                <p className="fs-7"><span className="fw-bold">Date of Birth</span><br/>{currentDeceased?.birthDateDisplay ? currentDeceased?.birthDateDisplay: "Unavailable"}</p>
                                <p className="fs-7"><span className="fw-bold">Cemetery</span><br/>{currentDeceased?.cemeteryName}</p>
                                <p className="fs-7"><span className="fw-bold">Yahrzeit</span><br/>{currentDeceased?.yahrzeitDesc ? currentDeceased?.yahrzeitDesc : "Unavailable"} {currentDeceased?.yahrzeitDateDisplay && "(" +  currentDeceased?.yahrzeitDateDisplay + ")"}</p>
                            </div>
                        </div>
                    </div>
                </li>
            );
            key++;
        });
        return result;
    }
    
    const processData = (responseJson: any) => {
        // setDeceasedList(responseJson.data);
    }

    const handleMessageSuccessShow = (e?: any) => {
        setFormBody(false);
        setFormMessageSuccess(true);
    }

    const handleMessageErrorShow = (e?: any) => {
        setFormBody(false);
        setFormMessageError(true);
    }

    const handleMessageHide = (e?: any) => {
        setTimeout( ()=> {
            setFormBody(true);
            setFormMessageSuccess(false);
            setFormMessageError(false);
        }, 500)
    }

    const processReceipt = (responseJson: ReceiptType) => {
        if(responseJson.paidInd) {
            handleMessageSuccessShow();
        } else {
            handleMessageErrorShow();
        }
    }

    const handleRemoveDeceased = (deceasedId: string) => {
        setDeceasedListSelected(prev => prev.filter(id => id !== deceasedId));
    }

    const handleSelectGrave = (deceased: DeceasedResult) => {
        setDeceasedList(prev => [...prev, deceased]);

        if (deceasedListSelected.length < 6 && !deceasedListSelected.some(id => id === deceased.deceasedId)) {
            setDeceasedListSelected(prev => [...prev, deceased.deceasedId]);
        }
    }

    const handleFormChange = (e: React.ChangeEvent<any>) => {
        if (e.target.type === "text" || e.target.type === "email") {
            let val = e.target.value;
            setFormState((formState) => ({
                ...formState, 
                [e.target.id]: val
            }));
        }

        if (e.target.type === "select-one") {
            const value = e.target.value;

            if (e.target.id === "selectDeceased") {
                if (deceasedListSelected.length < 6 && !deceasedListSelected.some(id => id === value)) {
                    setDeceasedListSelected(prev => [...prev, value]);
                }
            }

            if (e.target.id === "repeats") {
                setIsOnceOff(true)
                if (value === "1") {
                    setFormState((formState) => ({
                        ...formState,
                        monthlyAmount: deceasedListSelected.length * 360,
                        prepaidAmount: deceasedListSelected.length * 360,
                        months: 1,
                        prePaid: false
                    }));
                } else if (value === "2") {
                    setIsOnceOff(false)
                    setFormState((formState) => ({
                        ...formState,
                        monthlyAmount: (deceasedListSelected.length + 1) * 90,
                        prepaidAmount: (deceasedListSelected.length + 1) * 90 * 12,
                        months: 12
                    }));
                } else if (value === "3") {
                    setIsOnceOff(false)
                    setFormState((formState) => ({
                        ...formState,
                        monthlyAmount: (deceasedListSelected.length + 1) * 90,
                        prepaidAmount: (deceasedListSelected.length + 1) * 90 * 24,
                        months: 24
                    }));
                } else if (value === "4") {
                    setIsOnceOff(false)
                    setFormState((formState) => ({
                        ...formState,
                        monthlyAmount: (deceasedListSelected.length + 1) * 90,
                        prepaidAmount: (deceasedListSelected.length + 1) * 90 * 36,
                        months: 36
                    }));
                }
            }
        }


        if (e.target.type === "checkbox") {
            if (e.target.id === 'prePaid') {
                setFormState((formState) => ({
                    ...formState, 
                    [e.target.id]: e.target.checked
                }));
            }

            if (e.target.id === 'terms') {
                setTermsAccepted(e.target.checked);
            }

        }
    }

    const handlePost = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        fetchRecords(`subscriber/?subscriberEmail=${formState.subscriberEmail}`, processTransaction)
        .catch((error) => {
            console.log(error.message);
        });
    }

    const processTransaction = (responseJson: any) => {
        const updatedFormState: FormDataType = {
            ...formState,
            subscriberId: responseJson.id
        }

        postFormData(
            'maintenance_plan',
            updatedFormState,
            (response: any) => {
                getPaymentData(response);
                console.log("RESPONSE:::: ", response.id);
            },
            (error: any) => {
                console.log("ERROR : " + error);
            }
        );
    }


    return(
        <>
            <Helmet>
                <title>Grave Maintenance - Jewish Cemetery</title>
                <meta name="description" content="This website was designed by the Jewish Cemeteries Maintenance Board to pay tribute to loved ones that are no longer with us." />
            </Helmet>

            {payForm}
            <div className="jc-grid mx-auto" style={{maxWidth: "1028px"}}>

                <p className="jc-txt-heading fs-3 mb-4">Memorial Maintenance Plan</p>

                <form id="maintenanceForm" onSubmit={handlePost}>
                    <input id="currencydId" name="currencyId" type="hidden" value={currency?.id} onChange={handleFormChange} />

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <div className="d-flex flex-column gap-1 pb-2">
                                <label htmlFor="firstName" className="jc-txt-body fw-6">First Name</label>
                                <input  id="firstName" name="firstName" type="text" value={formState.firstName} onChange={handleFormChange} className="form-control form-input" placeholder="First Name" />
                            </div>
                        </div>

                        <div className="col-md-6 mb-3">
                            <div className="d-flex flex-column gap-1 pb-2">
                                <label htmlFor="lastName" className="jc-txt-body fw-6">Last Name</label>
                                <input  id="lastName" name="lastName" type="text" value={formState.lastName} onChange={handleFormChange} className="form-control form-input" placeholder="Last Name" />
                            </div>
                        </div>

                        <div className="col-md-6 mb-3">
                            <div className="d-flex flex-column gap-1 pb-2">
                                <label htmlFor="subscriberEmail" className="jc-txt-body fw-6">Email</label>
                                <input  id="subscriberEmail" name="subscriberEmail" type="email" value={formState.subscriberEmail} onChange={handleFormChange} className="form-control form-input" placeholder="Email Address" />
                            </div>
                        </div>

                        <div className="col-md-6 mb-3">
                            <div className="d-flex flex-column gap-1 pb-2">
                                <label htmlFor="telephone" className="jc-txt-body fw-6">Mobile</label>
                                <input id="telephone" name="telephone" type="text" value={formState.telephone} onChange={handleFormChange} className="form-control form-input" placeholder="Mobile Number" />
                            </div>
                        </div>

                        <div className="col-12 mb-3">
                            {/* <label htmlFor="selectDeceased" className="jc-txt-body fw-6">Select deceased</label>

                            <select id="selectDeceased" name="selectDeceased" className="form-select form-input" onChange={handleFormChange}>
                                <option value="" disabled selected>Select deceased</option>

                                {deceasedList?.map((deceased) => (
                                    <option value={deceased.deceasedId}>{deceased.firstName} {deceased.lastName}&nbsp;&nbsp;|&nbsp;&nbsp;{deceased.deathDateDisplay}</option>
                                ))}
                            </select> */}

                            <div className="search-wrapper">
                                <SearchDeceasedRecord graveMaintenance={true} selectGrave={handleSelectGrave} />
                            </div>
                        </div>

                        <div className="col-12">
                            {deceasedListJSX}

                            {deceasedListSelected.length > 0 &&
                                <>
                                    <div className="d-flex gap-3 mt-5">
                                        <select id="repeats" name="repeats" className="form-select form-input" onChange={handleFormChange} >
                                            <option value="0" selected disabled>Select payment plan</option>
                                            <option value="1">R{deceasedListSelected.length * 360} once off</option>
                                            <option value="2">R{(deceasedListSelected.length + 1) * 90} for 12 months</option>
                                            <option value="3">R{(deceasedListSelected.length + 1) * 90} for 24 months</option>
                                            <option value="4">R{(deceasedListSelected.length + 1) * 90} for 36 months</option>
                                        </select>
                                    </div>

                                    {!isOnceOff &&
                                        <div className="form-check d-flex gap-2 ps-5 mt-2">
                                            <input id="prePaid" name="prePaid" type="checkbox" className="form-check-input" checked={formState.prePaid} onChange={handleFormChange} />
                                            <label className="form-check-label jc-txt-body fw-6" htmlFor="yahrzeitReminderInd">Pay in full?</label>
                                        </div>
                                    }
                                </>
                            }

                        </div>
                    </div>
               
                    <p className="d-flex flex-column jc-txt-body jc-txt-dark fs-6 mt-2">
                        <span className="mb-4"><i>* Grave Maintenance is restricted to a once-off/ single transaction and a maximum of 6 graves per transaction.</i></span>

                        <span className="jc-txt-heading fs-4 mb-2">Terms & Conditions</span>
                        <span className="mb-4">Grave Maintenance is restricted to a once-off/ single transaction and a maximum of 6 graves per transaction. Recurring payment plan options are restricted to 12, 24 and 36 month periods. Once-off payments are accepted at a rate of R360 per grave. The Grave Maintenance plan includes cleaning and maintenance of the tombstone and surrounding area of the grave and excludes structural memorial work. Tombstones will be regularly inspected and notification sent should any repairs or structural memorial work be required. Quotes will be sourced for any repairs or structural memorial work, on request. T&C’s apply. E&OE.</span>
                    
                        <span className="mb-4"><span className="jc-txt-heading fs-5">Disclaimer: </span>Graves and tombstones are open to the natural elements and, whilst regularly maintained, are subject to the effects of adverse weather conditions and animal and bird life in and around the cemetery. Advance notice of visits to the cemetery will ensure that the graves of your loved ones are cleaned in preparation of your visit to the cemetery.</span>
                    </p>

                    {(deceasedListSelected.length > 0 && formState.monthlyAmount > 0) &&
                        <div className="d-flex flex-column gap-3 justify-content-center align-items-center">
                            <div className="form-check d-flex gap-2 ps-5 mt-2">
                                <input id="terms" name="terms" type="checkbox" className="form-check-input" checked={termsAccepted} onChange={handleFormChange} />
                                <label className="form-check-label jc-txt-body fw-6" htmlFor="yahrzeitReminderInd">Accept Terms & Conditions</label>
                            </div>
                            <button id="submit" type="submit" className="btn-submit btn btn-dark jc-txt-body jc-txt-light w-50" disabled={!termsAccepted}>Proceed</button>
                        </div>
                    }
                 </form>

            </div>
        </>
    );
}